import { Option } from "react-multi-select-component";
import { FnAuthenticatedRequest, SERVICES } from "../../Utility/Utils";
import { GTPhotoResponseDto, GtListRequestDto, GtSummaryListResponse, GtSummaryRequestDto, GtVisitsListResponseDto, GtVisitsRequestDto, GtVisitsResponseDto, QuestionResponseDto } from "./Dashboard.Type";

const getAuthHeader = () => {
    const authData = JSON.parse(localStorage.getItem("auth") || "{}");
    return {
        JWTToken: authData?.jwtToken,
        userId: authData?.userId,
    };
};

let questionCache: QuestionResponseDto[] = [];
let isQuestionCacheLoaded = false;
const DashboardService = {
    getGtSummary: async (
        request: GtSummaryRequestDto
    ): Promise<GtSummaryListResponse> => {

        const authHeader = getAuthHeader();

        try {
            const response = await FnAuthenticatedRequest(
                "POST",
                "Dashboard/GetGtSummary",
                authHeader,
                request,
                SERVICES.FKFIELD
            );
            return response?.data ?? {
                results: [],
                list: [],
                totalRecords: 0,
                pageSize: request?.pageSize,
                currentPage: request?.pageNo,
                pageCount: 0,
            };
        } catch (error) {
            throw error;
        }
    },
    getGtQuestions: async (
        forceRefresh: boolean = false
    ): Promise<QuestionResponseDto[]> => {

        // Return cached data if available
        if (!forceRefresh && isQuestionCacheLoaded) {
            return questionCache;
        }

        const authHeader = getAuthHeader();

        try {
            const response = await FnAuthenticatedRequest(
                "POST",
                "GetGtQuestions",
                authHeader,
                {},
                SERVICES.FKFIELD
            );

            // Save into cache
            questionCache = response?.data ?? [];
            isQuestionCacheLoaded = true;
            return questionCache;
        } catch (error) {
            throw error;
        }
    },
    downloadImage: async (visitId: string, fileName: string): Promise<any> => {
        const authHeader = getAuthHeader();

        try {
            const response = await FnAuthenticatedRequest(
                "POST",
                "DownloadPhoto",
                authHeader,
                { visitId, fileName },
                SERVICES.FKFIELD,
            );
            return response?.data;
        } catch (error) {
            throw error;
        }
    },
    downloadBulkImage: async (visitIds: string[]): Promise<any> => {
        const authHeader = getAuthHeader();
        const payload = { visitIds };
        try {
            const response = await FnAuthenticatedRequest(
                "POST",
                "DownloadBulkPhoto",
                authHeader,
                payload,
                SERVICES.FKFIELD,
                { responseType: 'blob' }
            );
            return response;
        } catch (error) {
            throw error;
        }
    },
    getStates: async (): Promise<Option[]> => {
        const authHeader = getAuthHeader();

        try {
            const response: any = await FnAuthenticatedRequest(
                "POST",
                "FieldActivity/GetStates",
                authHeader,
                "",
                SERVICES.FKFIELD
            );

            if (!response?.success) {
                return [];
            }

            if (!Array.isArray(response?.data)) {
                return [];
            }

            return response.data.map((state: any) => ({
                label: (state?.stateName ?? state?.statename ?? '').toUpperCase(),
                value: state?.stateId ?? state?.stateid,
            }));

        } catch (error) {
            throw error;
        }
    },
    getDistrict: async (arrTrIds: string[]): Promise<Option[]> => {
        const authHeader = getAuthHeader();
        try {
            const response: any = await FnAuthenticatedRequest(
                "POST",
                "TerritoryWiseDistrict",
                authHeader,
                arrTrIds,
                SERVICES.FKBO
            );

            if (!response?.success) {
                return [];
            }

            if (!Array.isArray(response?.data)) {
                return [];
            }

            return response.data.map((district: any) => ({
                label: (district?.districtName ?? district?.districtname ?? '').toUpperCase(),
                value: district?.districtId ?? district?.districtid,
            }));
        } catch (error) {
            throw error;
        }
    },
    getProjects: async (): Promise<Option[]> => {
        const authHeader = getAuthHeader();

        try {
            const response: any = await FnAuthenticatedRequest(
                "POST",
                "KycOverride/ProjectMaster",
                authHeader,
                "",
                SERVICES.KYC
            );

            if (!response?.success) {
                return [];
            }

            if (!Array.isArray(response?.data)) {
                return [];
            }

            return response?.data?.map((project: any) => ({
                label: project?.projectName?.toUpperCase(),
                value: project?.projectId,
                projectName: project?.projectName?.toUpperCase(),
            }));
        } catch (error) {
            throw error;
        }
    },
    getGtList: async (request: GtListRequestDto): Promise<Option[]> => {
        const authHeader = getAuthHeader();
        const payload = {
            stateName: request.stateName || "",
            isStateNotMapped: !!request.isStateNotMapped,
            territoryName: request.territoryName || "",
            isTerritoryNotMapped: !!request.isTerritoryNotMapped,
            districtName: request.districtName || "",
            isDistrictNotMapped: !!request.isDistrictNotMapped,
        };
        try {
            const response: any = await FnAuthenticatedRequest(
                "POST",
                "GTList",
                authHeader,
                payload,
                SERVICES.FKFIELD
            );

            if (!response?.success) {
                return [];
            }

            if (!Array.isArray(response?.data)) {
                return [];
            }

            return response?.data?.map((gt: any) => ({
                label: gt?.gtId,
                value: gt?.gtId,
            }));
        } catch (error) {
            throw error;
        }
    },
    getVisitByGt: async (gtId: string): Promise<Option[]> => {
        const authHeader = getAuthHeader();
        const payload = { gtId: gtId, type: "GT" };
        try {
            const response: any = await FnAuthenticatedRequest(
                "POST",
                "GTVisitList",
                authHeader,
                payload,
                SERVICES.FKFIELD
            );

            if (!response?.success) {
                return [];
            }

            if (!Array.isArray(response?.data)) {
                return [];
            }

            return response?.data?.map((visit: any) => ({
                label: visit?.visitId,
                value: visit?.visitId,
            }));
        } catch (error) {
            throw error;
        }
    },
    downloadReport: async (request: GtSummaryRequestDto): Promise<any> => {
        const authHeader = getAuthHeader();

        try {
            const response = await FnAuthenticatedRequest(
                "POST",
                "DownloadGtReport",
                authHeader,
                request,
                SERVICES.FKFIELD,
                { responseType: 'blob' }
            );

            return response;
        } catch (error) {
            throw error;
        }
    },
    getVisitByDate: async (request: GtVisitsRequestDto): Promise<GtVisitsListResponseDto> => {
        const authHeader = getAuthHeader();
        const payload = { ...request };
        try {
            const response: any = await FnAuthenticatedRequest(
                "POST",
                "VisitsByDate",
                authHeader,
                payload,
                SERVICES.FKFIELD
            );

            if (!response?.success) {
                return { gtData: [], maxPhotoDownload: 50 };
            }

            if (!Array.isArray(response?.data?.gtData)) {
                return { gtData: [], maxPhotoDownload: 50 };
            }

            return {
                gtData: response?.data?.gtData?.map((visit: any) => ({
                    gtId: visit?.gtId,
                    visitId: visit?.visitId,
                    visitName: visit?.visitName,
                    imageCount: visit?.imageCount,
                })),
                maxPhotoDownload: response?.data?.maxPhotoDownload || 50
            };
        } catch (error) {
            throw error;
        }
    },
    getStateWiseTerritory: async (stateIds: string[]): Promise<Option[]> => {
        const authHeader = getAuthHeader();
        try {
            const response: any = await FnAuthenticatedRequest(
                "POST",
                "GetTerritoryDetails",
                authHeader,
                stateIds,
                SERVICES.FKBO // this api exists in FKBO service
            );

            if (!response?.success) {
                return [];
            }

            if (!Array.isArray(response?.data)) {
                return [];
            }

            return response.data.map((territory: any) => ({
                label: (territory?.territoryName ?? '').toUpperCase(),
                value: territory?.territoryId,

            }));
        } catch (error) {
            throw error;
        }
    }
};
export default DashboardService;